/**
 * Shared disclosure content for notice pages and Signature page modal.
 */

import type { CreditBureauConfig } from "@/lib/api/applicationService";
import type { DisclosureLinkId } from "@/features/applicant/types/pages";

const CFPB_LEARNMORE = "https://www.consumerfinance.gov/learnmore";
const CFPB_LEARNMORE_TEXT = "www.consumerfinance.gov/learnmore";

export const FAIR_HOUSING_TITLE =
  "A Summary of Your Rights Under the Fair Credit Reporting Act";

export const FAIR_HOUSING_CONTENT = `
  <p>Para informatión en español, visite <a href="${CFPB_LEARNMORE}" target="_blank" rel="noopener noreferrer">${CFPB_LEARNMORE_TEXT}</a> o escribe a la Consumer Financial Protection Bureau, 1700 G Street N.W., Washington, DC 20552.</p>
  <h2>A Summary of Your Rights Under the Fair Credit Reporting Act</h2>
  <p>The federal Fair Credit Reporting Act (FCRA) promotes the accuracy, fairness, and privacy of information in the files of consumer reporting agencies. There are many types of consumer reporting agencies, including credit bureaus and specialty agencies (such as agencies that sell information about check writing histories, medical records, and rental history records). <strong>For more information, including information about additional rights, go to <a href="${CFPB_LEARNMORE}" target="_blank" rel="noopener noreferrer">${CFPB_LEARNMORE_TEXT}</a> or write to: Consumer Financial Protection Bureau, 1700 G Street N.W., Washington, DC 20552.</strong></p>
  <h3>Your Rights Under the FCRA</h3>
  <p>You have the right to:</p>
  <ul>
    <li>Be told if information in your file has been used against you</li>
    <li>Know what is in your file</li>
    <li>Request a free copy of your file</li>
    <li>Dispute incomplete or inaccurate information</li>
    <li>Have consumer reporting agencies correct or delete inaccurate, incomplete, or unverifiable information</li>
    <li>Limit prescreened offers of credit and insurance you get based on information in credit reports</li>
    <li>Seek damages from violators</li>
  </ul>
  <h3>Additional Rights</h3>
  <p>You have additional rights if you are a victim of identity theft or if you are on active military duty. For more information, visit <a href="${CFPB_LEARNMORE}" target="_blank" rel="noopener noreferrer">${CFPB_LEARNMORE_TEXT}</a>.</p>
  <h3>For More Information</h3>
  <p>For more information, including information about specific rights of victims of identity theft and active duty military consumers, visit <a href="${CFPB_LEARNMORE}" target="_blank" rel="noopener noreferrer">${CFPB_LEARNMORE_TEXT}</a> or write to: Consumer Response Center, Room 130-A, Federal Trade Commission, 600 Pennsylvania Ave. N.W., Washington, D.C. 20580.</p>
`;

export const CREDIT_REPORTING_TITLE =
  "Notice Regarding Background Investigation Pursuant to California Law";

export function getCreditReportingContent(config?: CreditBureauConfig | null): string {
  const name = config?.details?.config_value || "Equifax Information Services, LLC";
  const address = config?.details
    ? `${config.details.cb_addressline1 || ""}<br />${config.details.cb_city || ""}, ${config.details.cb_state || ""} ${config.details.cb_zipcode || ""}`
    : "P.O. Box 740256<br />Atlanta, GA 30374";
  const phone = config?.details?.cb_telephone || "(866) 349-5191";
  const fax = config?.details?.cb_fax ? `<br />Fax: ${config.details.cb_fax}` : "";
  const isEq = config?.active === "Equifax" || !config;
  const web = isEq
    ? `<br />Web: <a href="https://www.equifax.com/personal/" target="_blank" rel="noopener noreferrer">https://www.equifax.com/personal/</a>`
    : "";

  return `
  <h2>Notice Regarding Background Investigation Pursuant to California Law</h2>
  <p>This notice is provided to you pursuant to California Civil Code section 1786.22 and the federal Fair Credit Reporting Act (15 U.S.C. § 1681 et seq.).</p>
  
  <h3>Background Investigation</h3>
  <p>As part of the application process, we may obtain information about you from a third party consumer reporting agency for employment, tenant screening, or other permissible purposes. This may include information about your character, general reputation, personal characteristics, or mode of living.</p>
  
  <h3>Your Rights</h3>
  <p>You have the right to:</p>
  <ul>
    <li>Request a copy of the consumer report that was used</li>
    <li>Request the name, address, and telephone number of the consumer reporting agency that furnished the report</li>
    <li>Dispute the accuracy or completeness of any information in the report</li>
  </ul>
  
  <h3>Consumer Reporting Agency</h3>
  <p>The consumer reporting agency that prepared this report is:</p>
  <p><strong>${name}</strong><br />
  ${address}<br />
  Phone: ${phone}${fax}${web}</p>
  
  <h3>Additional Information</h3>
  <p>You may obtain information about your rights under the Fair Credit Reporting Act by visiting <a href="https://www.consumer.ftc.gov" target="_blank" rel="noopener noreferrer">www.consumer.ftc.gov</a> or by writing to the Consumer Response Center, Room 130-A, Federal Trade Commission, 600 Pennsylvania Ave. N.W., Washington, D.C. 20580.</p>
`;
}

export const CREDIT_REPORTING_CONTENT = getCreditReportingContent();

export const AUTH_LIBERTY_TITLE =
  "Authorization for Liberty Rent to Collect Payments";

export const AUTH_LIBERTY_CONTENT = `
  <h2>Authorization for Liberty Rent to Collect Payments</h2>
  <p>By signing this authorization, you authorize Liberty Rent to collect payments on your behalf in connection with your rental application and lease agreement.</p>
  
  <h3>Payment Authorization</h3>
  <p>You authorize Liberty Rent to:</p>
  <ul>
    <li>Collect application fees and certificate fees</li>
    <li>Process payments for rent and related charges</li>
    <li>Use the payment information you provide for these purposes</li>
    <li>Share payment information with property management as necessary</li>
  </ul>
  
  <h3>Payment Methods</h3>
  <p>Liberty Rent accepts various payment methods including credit cards, debit cards, and electronic bank transfers. All payment processing is handled securely through our payment partners.</p>
  
  <h3>Fee Information</h3>
  <p><strong>Application Fee:</strong> The Liberty Rent Application Fee is Non-Refundable, unless you are Denied by Liberty Rent.</p>
  <p><strong>Certificate Fee:</strong> The Liberty Rent Certificate Fee is Non-Refundable.</p>
  
  <h3>Your Rights</h3>
  <p>You have the right to:</p>
  <ul>
    <li>Request information about any charges</li>
    <li>Dispute any unauthorized charges</li>
    <li>Cancel this authorization at any time by contacting us</li>
  </ul>
  
  <h3>Contact Information</h3>
  <p>If you have questions about this authorization or your payments, please contact:</p>
  <p><strong>Liberty Rent</strong><br />
  Email: <a href="mailto:help@libertyrent.com">help@libertyrent.com</a><br />
  Phone: (877) 581-7368</p>
  
  <h3>Agreement</h3>
  <p>By proceeding with your application, you acknowledge that you have read, understand, and agree to this Authorization for Liberty Rent to Collect Payments.</p>
`;

export function getDisclosureTitle(linkId: DisclosureLinkId): string {
  switch (linkId) {
    case 1:
      return FAIR_HOUSING_TITLE;
    case 2:
      return CREDIT_REPORTING_TITLE;
    case 3:
      return AUTH_LIBERTY_TITLE;
  }
}

export function getDisclosureContent(linkId: DisclosureLinkId, creditBureauConfig?: CreditBureauConfig | null): string {
  switch (linkId) {
    case 1:
      return FAIR_HOUSING_CONTENT;
    case 2:
      return getCreditReportingContent(creditBureauConfig);
    case 3:
      return AUTH_LIBERTY_CONTENT;
  }
}
